import { useState } from 'preact/hooks';
import { SlideOver } from '@components/slide-over';
import { showToast } from '@components/toaster';
import { rpx } from 'client/lib/rpx-client';
import { FeedbackCategory } from './types';

const categories: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'question', label: 'Question' },
  { value: 'content', label: 'Content' },
];

type Props = {
  pagePath: string;
  reporterName: string;
  onClose: () => void;
};

export function FeedbackForm({ pagePath, reporterName, onClose }: Props) {
  const [category, setCategory] = useState<FeedbackCategory>('improvement');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!comment.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await rpx.feedback.submitFeedback({
        pagePath,
        category,
        comment: comment.trim(),
        reporterName,
      });
      showToast({
        type: 'ok',
        title: 'Feedback submitted',
        message: `Created issue #${result.issueNumber}`,
      });
      onClose();
    } catch {
      showToast({
        type: 'warn',
        title: 'Submission failed',
        message: 'Could not create feedback issue. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SlideOver close={onClose} position="left" w="max-w-md w-screen">
      <h2 class="text-lg font-semibold mb-4">Submit Feedback</h2>

      <div class="mb-4 text-sm text-gray-500">
        <span class="font-medium text-gray-700">Page:</span> {pagePath}
      </div>

      <form onSubmit={handleSubmit} class="flex flex-col gap-4">
        <fieldset>
          <legend class="text-sm font-medium text-gray-700 mb-2">Category</legend>
          <div class="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <label
                key={cat.value}
                class={`cursor-pointer px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  category === cat.value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="category"
                  value={cat.value}
                  checked={category === cat.value}
                  onChange={() => setCategory(cat.value)}
                  class="sr-only"
                />
                {cat.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Comment</label>
          <textarea
            class="w-full border border-gray-300 rounded-md p-2 text-sm resize-y min-h-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={comment}
            onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)}
            placeholder="Describe the issue or suggestion…"
            required
          />
        </div>

        <div class="flex gap-2 justify-end">
          <button
            type="button"
            class="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !comment.trim()}
            class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </form>
    </SlideOver>
  );
}
